// SEC-055 (#373) PR #546 리뷰: 사유 코드 → 사용자 문구 매핑 SSoT.
//
// 배경: 발급 모달이 `setError(result?.reason)` 로 **영문 사유 코드를 그대로 노출**했다
// (할당 모달에만 로컬 맵이 있던 비대칭). 두 번째 사용처가 생겨 `lib/licenseErrorMessages.js` 로
// 추출했고, 여기서 두 맥락 맵의 계약을 값으로 고정한다 — 종전에는 모달 파일의 소스 텍스트
// grep 이 유일한 가드였다.
//
// IMP-437: 맵의 값을 한국어 하드코딩 문자열에서 **i18n 키**로 전환했다. 문구 해석 책임은
// 이 SSoT(resolveLicenseErrorMessage) 한 곳에 유지 — 호출처가 각자 t() 하지 않는다.
// 함수형 엔트리(INSUFFICIENT_*)는 `{key, vars}` 를 반환하고 `interpolate(t(key), vars)` 로
// 조합한다. 19개 언어 키 값 자체는 `src/config/lang/*.json` (병행 작업, 이 파일 범위 밖).
//
// 실행: cd repos/rayguardv2-front-end && CI=true npx react-scripts test --watchAll=false
//   src/lib/__tests__/licenseErrorMessages.test.js

import { interpolate } from '../config/i18nInterpolate';

// 맥락 무관 공통 문구.
const COMMON = {
    // FEAT-024 AC-8 — 1회 자동 재시도 후에도 충돌 시 사용자가 직접 재시도하도록 안내.
    // ALLOCATION_UPDATE_CONFLICT 와 REVOKE_UPDATE_CONFLICT 는 문면이 동일하므로 키 하나를 공유한다.
    ALLOCATION_UPDATE_CONFLICT: 'license.error.allocationUpdateConflict',
    REVOKE_UPDATE_CONFLICT: 'license.error.allocationUpdateConflict',
    DEALER_ALLOCATION_NOT_FOUND: 'license.error.dealerAllocationNotFound',
    CORP_ALLOCATION_NOT_FOUND: 'license.error.corpAllocationNotFound',
    GATEWAY_ERROR: 'license.error.gatewayError',
    // PR #546 R2 — 양쪽 thunk 가 공통으로 반환하는 코드. 종전에는 할당 맵에 없어 인라인 폴백이
    // 영문 코드를 노출했다(`UNAUTHENTICATED` 등).
    UNAUTHENTICATED: 'license.error.unauthenticated',
    INVALID_IDENTIFIER: 'license.error.invalidIdentifier',
};

// 할당·회수 모달(AllocateDealerModal).
export const ALLOCATION_ERROR_MESSAGES = {
    ...COMMON,
    INSUFFICIENT_CORP_QUOTA: (issuable) => ({
        key: 'license.error.insufficientCorpQuota',
        vars: { newQty: issuable?.newQty ?? 0, renewQty: issuable?.renewQty ?? 0 },
    }),
    INSUFFICIENT_DEALER_QUOTA: (issuable) => ({
        key: 'license.error.insufficientDealerQuota',
        vars: { newQty: issuable?.newQty ?? 0, renewQty: issuable?.renewQty ?? 0 },
    }),
    // FEAT-050 S5b: 할당 대상이 권한 범위(회사 계층) 밖. 발급 맥락과 문면이 달라 별도 키를 쓴다.
    TARGET_OUT_OF_SCOPE: 'license.error.allocationTargetOutOfScope',
    // BUG-200 PR #535: §12.1-A 로 자기할당이 tier 무관 차단으로 확대돼 "중간 법인" 한정 문구를
    // 정정. (reason 코드는 하위 호환을 위해 SELF_ALLOCATION_MIDCORP 유지.)
    SELF_ALLOCATION_MIDCORP: 'license.error.selfAllocationBlocked',
    // BUG-200 PR #535: RevokeFromDealer 자기회수 차단 (SmartDentAction.js 대칭 가드).
    SELF_REVOKE_BLOCKED: 'license.error.selfRevokeBlocked',
    // BUG-198 v2: 순차 할당 — 직속 자식이 아닌 대상(손자 등) 할당/회수 차단.
    TARGET_NOT_DIRECT_CHILD: 'license.error.targetNotDirectChild',
    // PR #546 R2 — 인라인 폴백이 노출하던 나머지 코드.
    UNAUTHORIZED: 'license.error.unauthorized',
    INVALID_QUANTITY: 'license.error.invalidQuantity',
};

// 발급(QR) 모달(VoucherPrintModal).
export const ISSUANCE_ERROR_MESSAGES = {
    ...COMMON,
    INSUFFICIENT_QUOTA: (issuable) => ({
        key: 'license.error.insufficientIssuanceQuota',
        vars: { issuable: issuable ?? 0 },
    }),
    // SEC-055: 발급 대상(dealerId) 스코프 밖 — 자기 또는 직속 하위만 허용.
    // BUG-196 의 예산 주체 가드도 같은 코드로 수렴하므로(정보 노출 차단) 문면을 둘 다 포괄한다.
    TARGET_OUT_OF_SCOPE: 'license.error.issuanceTargetOutOfScope',
    COUNT_EXCEEDS_MAX: 'license.error.countExceedsMax',
    INVALID_COUNT: 'license.error.invalidCount',
    INVALID_TYPE: 'license.error.invalidType',
    INVALID_VALIDUNTIL: 'license.error.invalidValidUntil',
    // PR #546 R2 — 손유지 목록에서 누락됐던 3코드(테스트가 소스에서 직접 추출하도록 바뀜).
    CORP_MANAGER_GROUP_ID_MISMATCH: 'license.error.corpManagerGroupIdMismatch',
    QUOTA_UPDATE_CONFLICT: 'license.error.quotaUpdateConflict',
    // 일부만 기록된 상태 — 사용자가 발급 이력을 확인하도록 안내한다(재시도 시 중복 발급 위험).
    QR_WRITE_PARTIAL: 'license.error.qrWritePartial',
};

// 매핑 부재 시 문구 키. 호출처가 `translate(ALLOCATION_FALLBACK_MESSAGE)` 로 직접 해석해
// `resolveLicenseErrorMessage` 의 `fallback` 인자(이미 번역된 문자열)로 전달한다.
export const ALLOCATION_FALLBACK_MESSAGE = 'license.error.allocationFallback';

/**
 * reason 코드를 사용자 문구로 변환한다. 매핑이 없으면 `fallback` 을 쓴다 —
 * **영문 코드를 그대로 노출하지 않는다**(SEC-055 리뷰).
 *
 * IMP-437: 맵의 값이 i18n 키(문자열) 또는 `(arg) => { key, vars }` 함수로 바뀌었다.
 * `translate` 가 함수가 아니면(호출처 오용 방어) 안전하게 fallback 을 반환한다.
 *
 * @param {object} map ALLOCATION_ERROR_MESSAGES | ISSUANCE_ERROR_MESSAGES | LICENSE_DETAIL_ERROR_MESSAGES
 * @param {string} reason thunk 가 반환한 사유 코드
 * @param {*} arg 함수형 문구에 전달할 인자(가용 수량 등)
 * @param {(key: string) => string} translate `Translate as t` — i18n 키 → 문구.
 * @param {string} fallback 매핑 부재 시 문구(이미 번역된 텍스트).
 */
export const resolveLicenseErrorMessage = (map, reason, arg, translate, fallback) => {
    if (typeof translate !== 'function') return fallback;
    const entry = map?.[reason];
    if (typeof entry === 'function') {
        const { key, vars } = entry(arg) || {};
        if (!key) return fallback;
        return interpolate(translate(key), vars);
    }
    if (typeof entry === 'string') return translate(entry);
    // IMP-437: 매핑 미스는 사용자에게 폴백 문구로 안전하게 처리되지만, **어떤 코드가 미매핑인지가
    //   로그에서도 사라지면** BE 가 신규 reason 을 추가해도 무감지다(CLAUDE.md §7 관측성).
    //   reason 이 실제로 온 경우에만 warn — reason 부재(null/undefined)는 정상 폴백 경로다.
    if (typeof reason === 'string' && reason.trim() !== '') {
        console.warn('[i18n] unmapped license error reason', {
            handler: 'lib.licenseErrorMessages.resolveLicenseErrorMessage',
            reason: reason.slice(0, 64),
            mappedCount: Object.keys(map || {}).length,
            note: 'fallback 문구로 표시됨 — 신규 BE 코드면 맵과 19개 언어 키를 추가할 것',
        });
    }
    return fallback;
};

// IMP-437 (D): LicenseDetailModal 의 메모 저장 / Suspend / Resume 실패 시 BE reason 코드가
// 화면에 그대로 노출되던 것을 해소. 새 매핑 파일을 만들지 않고 이 SSoT 를 확장한다(사용자 확정).
// 코드 목록은 src/actions/SmartDentAction.js 의 UpdateLicenseMemo/SuspendLicense/ResumeLicense
// 반환문에서 추출(2026-08-10 실측). COMMON 공유 코드(INVALID_IDENTIFIER/UNAUTHENTICATED/
// GATEWAY_ERROR)는 스프레드로 재사용 — 나머지는 매핑 없으면 폴백(fail-loud, 코드 원문 미노출).
export const LICENSE_DETAIL_ERROR_MESSAGES = {
    ...COMMON,
    MEMO_TOO_LONG: 'license.error.memoTooLong',
    MEMO_CONFLICT: 'license.error.memoConflict',
    INVALID_MEMO: 'license.error.invalidMemo',
    LICENSE_NOT_VISIBLE: 'license.error.licenseNotVisible',
    SUSPEND_CONFLICT_OR_MISSING: 'license.error.suspendConflictOrMissing',
    RESUME_CONFLICT_OR_MISSING: 'license.error.resumeConflictOrMissing',
    // PR 리뷰 Suggestion#2: `license.error.forbidden` 과 `unauthorized` 의 값이 19개 언어 전부
    //   동일했다 — 이 파일이 세운 "문면 동일 시 키 공유" 규칙(ALLOCATION/REVOKE_UPDATE_CONFLICT,
    //   EMPTY/INVALID_RESPONSE)의 유일한 예외였다. 통합하고 forbidden 키는 폐기한다.
    FORBIDDEN: 'license.error.unauthorized',
    // PR 사전 리뷰 Minor#2: 위 7코드만 매핑하면 `NETWORK_ERROR` 처럼 **일상적으로 발생하는 정상
    //   실패 경로**가 매번 미매핑 warn 을 찍어 "신규 BE 코드 감지" 신호가 희석된다. warn 이
    //   신호로 기능하려면 *알려진 코드는 전부* 매핑돼 있어야 하므로, SmartDentAction 의
    //   UpdateLicenseMemo/SuspendLicense/ResumeLicense 가 반환하는 나머지 5코드도 채운다.
    NETWORK_ERROR: 'license.error.networkError',
    LICENSE_NOT_FOUND: 'license.error.licenseNotFound',
    // sk(sort key) 형식 오류 — 사용자 관점에서는 "대상 식별자가 올바르지 않다" 와 동일하므로
    //   기존 키를 재사용한다(중복 키 생성 회피).
    INVALID_SK: 'license.error.invalidIdentifier',
    // 응답 자체가 비었거나 형식이 어긋난 경우 — 사용자 조치는 동일하므로 한 키를 공유한다.
    EMPTY_RESPONSE: 'license.error.invalidServerResponse',
    INVALID_RESPONSE: 'license.error.invalidServerResponse',
};

// PR 리뷰 Major (Security/CQ 수렴): VoucherTemplateEditor 만 `t(result?.reason || 'Save failed')` 로
//   **BE reason 코드를 직접 i18n 키로** 쓰고 있었다. `SaveVoucherTemplate` /
//   `ResetVoucherTemplateToHqDefault` 는 IDOR 가드(`assertCallerOwnsCompanyId`)가 반환하는
//   `INVALID_COMPANY_ID` / `MISSING_ACTOR_COMPANY_ID` / `COMPANY_ID_MISMATCH` 를 그대로 reason 으로
//   올리는데 이 3코드는 19개 언어 어디에도 없어(0/19) `Translate` 가 키 원문을 반환했다 →
//   **내부 인가 가드 코드가 사용자 배너에 영문 그대로 노출**(SEC-055 결함 재발 유형).
//   `t(rawCode)` 는 미매핑 시 코드를 노출하는 fail-open 이라 BE 가 코드를 추가할 때마다 같은
//   누출이 재발하고, 이 파일의 미매핑 warn 감지망도 타지 않는다. SSoT 로 흡수한다.
//
//   가드 3코드는 **어느 가드가 걸렸는지 구분해 보여주지 않는다** — 사용자 조치는 동일하고,
//   구분 노출은 그 자체가 내부 인가 구조 힌트다(개발자용 식별은 SmartDentAction 의 기존
//   console.warn 이 이미 담당). 기존 `unauthorized` 키를 재사용해 신규 키도 만들지 않는다.
//   나머지 3코드는 이미 19/19 인 flat 키를 그대로 쓴다(중복 키 생성 회피).
export const VOUCHER_TEMPLATE_ERROR_MESSAGES = {
    GATEWAY_ERROR: 'license.error.gatewayError',
    INVALID_QR_BOX: 'INVALID_QR_BOX',
    TEMPLATE_UPDATE_CONFLICT: 'TEMPLATE_UPDATE_CONFLICT',
    RESET_NO_UPDATED_FIELD: 'RESET_NO_UPDATED_FIELD',
    INVALID_COMPANY_ID: 'license.error.unauthorized',
    MISSING_ACTOR_COMPANY_ID: 'license.error.unauthorized',
    COMPANY_ID_MISMATCH: 'license.error.unauthorized',
    INVALID_IDENTIFIER: 'license.error.invalidIdentifier',
};
